import { Button } from '@aqarmap/aqarmap-ui';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { useTranslation } from 'react-i18next';
import { InlineCard } from '~/components/ListingCard';
import { useGetListingsQuery } from '~/redux/api/appApiSlice';
import { useBreakpoint } from '~/hooks';
import relatedListingsSliderBreakPoints from '~valuation/utils/relatedListingsSliderBreakPoints';
import { ListingsResponseType } from '~/types/listingTypes';
import useSwiperNavigation from '~valuation/hooks/useSwiperNavigation';
import SliderNavigation from '~valuation/pages/ValuationResults/RelatedListings/SliderNavigation';
import RelatedListingsLoader from '~valuation/pages/ValuationResults/RelatedListings/RelatedListingsLoader';

export default function RelatedListings({
  propertyType,
  location,
  section,
}: {
  propertyType: { id: number; slug: string };
  location: { id: number; slug: string };
  section: { id: number; slug: string };
}) {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const { mobileScreen } = useBreakpoint();
  const navigation = useSwiperNavigation();
  const { sliderRef, handleInit, handleSlideChange, ...navigationProps } =
    navigation;
  const { data, isLoading } = useGetListingsQuery({
    locale,
    location: location.id,
    propertyType: propertyType.id,
    section: section.id,
  });

  if (isLoading) {
    return <RelatedListingsLoader />;
  }
  if (!data?.default?.data || data?.default?.data.length === 0) {
    return null;
  }
  const listings = data.default.data;
  const discoverMoreUrl = `${process.env.REACT_APP_BASE_URL}/${locale}/${section.slug}/${propertyType.slug}/${location.slug}`;

  return (
    <div className="flex flex-col gap-3x ">
      <div className="flex justify-between items-center">
        <p className="text-title_4">{t('relatedListingTitle')}</p>
        {mobileScreen ? (
          <a
            href={discoverMoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-link text-blue line"
          >
            {t('discoverMore')}
          </a>
        ) : (
          <Button
            variant="secondary"
            size="medium"
            onClick={() => window.open(discoverMoreUrl)}
          >
            {t('discoverMore')}
          </Button>
        )}
      </div>
      <div className="relative related-listings-slider">
        <SliderNavigation {...navigationProps} />
        <Swiper
          ref={sliderRef}
          onInit={handleInit}
          onSlideChange={handleSlideChange}
          breakpoints={relatedListingsSliderBreakPoints}
          mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
          touchStartPreventDefault={false}
          passiveListeners={false}
          modules={[Pagination]}
          pagination={mobileScreen ? { clickable: true } : false}
          className="h-[132px] md:h-[118px]"
        >
          {listings?.map((listing: ListingsResponseType) => (
            <SwiperSlide key={listing.id} className="md:!w-[343px]">
              <InlineCard
                className="border border-gray related-listing-card"
                key={`mobile-map-card-${listing.id}`}
                listing={{
                  address: listing.address,
                  id: listing.id,
                  price: listing.price,
                  area: listing.area,
                  attributes: listing.attributes,
                  slug: listing.slug,
                  section: listing.section,
                  title: listing.title,
                  video_url: listing.video_url ?? '',
                  photo: listing.photos[0]?.thumbnails?.thumb || '',
                  photosCount: listing.photos.length,
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
